import { PersistenceAdapterPort } from './PersistenceAdapterPort';
import { safeNodeImport } from '../../utils/dynamicImport';
import { getEnvironment } from '../../config/environment';

// Type definition for Redis client
type RedisClientType = {
  get(key: string): Promise<string | null>;
  set(
    key: string,
    value: string,
    mode?: string,
    duration?: number
  ): Promise<void>;
  del(key: string): Promise<void>;
};

// Type for Redis client options
interface RedisOptions {
  host?: string;
  port?: number;
  maxRetriesPerRequest?: number;
  retryStrategy?: (times: number) => number;
  connectTimeout?: number;
  enableOfflineQueue?: boolean;
  [key: string]: unknown;
}

// Type for the dynamic Redis import
interface RedisModule {
  default: new (options?: RedisOptions | string) => RedisClientType;
}

/**
 * Environment-aware Redis adapter factory
 * Creates the appropriate implementation based on the runtime environment
 */
export const createPersistenceAdapter = (): PersistenceAdapterPort => {
  const env = getEnvironment();

  // Browser environment check
  if (typeof window !== 'undefined') {
    // For browser with direct Redis connection
    if (env.storageType === 'dynamoDB' && (env.redisHost || env.redisPort)) {
      console.log('Creating WebSocket Redis adapter for browser');
      return new BrowserRedisAdapter();
    }

    // Default browser storage
    return new BrowserStorageAdapter();
  }

  // Server/Node.js environment
  return new NodeRedisAdapter();
};

/**
 * Browser-compatible implementation using localStorage
 */
class BrowserStorageAdapter implements PersistenceAdapterPort {
  private expiryKey(key: string): string {
    return `${key}:expiry`;
  }

  async get(key: string): Promise<string | null> {
    // Check if key exists and isn't expired
    const expiryStr = localStorage.getItem(this.expiryKey(key));
    if (expiryStr) {
      const expiry = parseInt(expiryStr, 10);
      if (expiry < Date.now()) {
        // Expired, clean up and return null
        localStorage.removeItem(key);
        localStorage.removeItem(this.expiryKey(key));
        return null;
      }
    }
    return localStorage.getItem(key);
  }

  async set(key: string, value: string, ttlSeconds?: number): Promise<void> {
    localStorage.setItem(key, value);

    // If TTL provided, store expiration timestamp
    if (ttlSeconds) {
      const expiryTime = Date.now() + ttlSeconds * 1000;
      localStorage.setItem(this.expiryKey(key), expiryTime.toString());
    } else {
      // Remove any existing expiry
      localStorage.removeItem(this.expiryKey(key));
    }
  }

  async del(key: string): Promise<void> {
    localStorage.removeItem(key);
    localStorage.removeItem(this.expiryKey(key));
  }
}

/**
 * Browser Redis adapter using REST API calls to Redis
 * This is a fallback option when direct socket connections aren't available
 *
 * Note: This requires CORS to be enabled on Redis or using a CORS proxy
 */
class BrowserRedisAdapter implements PersistenceAdapterPort {
  private baseUrl: string;

  constructor() {
    const env = getEnvironment();
    const host = env.redisHost || 'localhost';
    const port = env.redisPort || 6379;

    // In real implementation, you would need a Redis REST API proxy
    // For this example, we're simulating with a URL structure
    this.baseUrl = `http://${host}:${port}/redis`;

    console.log(
      `Initialized BrowserRedisAdapter with endpoint: ${this.baseUrl}`
    );
  }

  /**
   * Warning: This is a simplified implementation that demonstrates the pattern.
   * In a real application, you would:
   * 1. Use a Redis proxy server with REST API (like redis-commander or a custom service)
   * 2. Handle authentication and security
   * 3. Properly handle errors and reconnection
   */

  async get(key: string): Promise<string | null> {
    try {
      console.log(`Fetching key ${key} from Redis via HTTP`);

      // This is a placeholder - in reality, you'd need a Redis HTTP proxy
      // The code below is simplified for demonstration purposes
      const response = await fetch(
        `${this.baseUrl}/get/${encodeURIComponent(key)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        console.warn(`Failed to get key ${key}: ${response.statusText}`);
        return null;
      }

      const data = await response.json();
      return data.value;
    } catch (error) {
      console.error('Redis HTTP get error:', error);
      // Fall back to localStorage in case of error
      return localStorage.getItem(key);
    }
  }

  async set(key: string, value: string, ttlSeconds?: number): Promise<void> {
    try {
      console.log(`Setting key ${key} in Redis via HTTP`);

      // This is a placeholder - in reality, you'd need a Redis HTTP proxy
      // The code below is simplified for demonstration purposes
      const response = await fetch(`${this.baseUrl}/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key,
          value,
          ttl: ttlSeconds,
        }),
      });

      if (!response.ok) {
        console.warn(`Failed to set key ${key}: ${response.statusText}`);
        // Fall back to localStorage
        localStorage.setItem(key, value);
      }
    } catch (error) {
      console.error('Redis HTTP set error:', error);
      // Fall back to localStorage in case of error
      localStorage.setItem(key, value);
    }
  }

  async del(key: string): Promise<void> {
    try {
      console.log(`Deleting key ${key} from Redis via HTTP`);

      // This is a placeholder - in reality, you'd need a Redis HTTP proxy
      // The code below is simplified for demonstration purposes
      const response = await fetch(
        `${this.baseUrl}/del/${encodeURIComponent(key)}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        console.warn(`Failed to delete key ${key}: ${response.statusText}`);
        // Fall back to localStorage
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.error('Redis HTTP del error:', error);
      // Fall back to localStorage in case of error
      localStorage.removeItem(key);
    }
  }
}

/**
 * Node.js implementation using ioredis
 * This code will only be executed in Node.js environments
 */
class NodeRedisAdapter implements PersistenceAdapterPort {
  private client: RedisClientType | null = null;

  constructor() {
    // Initialization happens asynchronously
    this.initRedis().catch(error => {
      console.error('Failed to initialize Redis client', error);
    });
  }

  private async initRedis(): Promise<void> {
    if (typeof window !== 'undefined') {
      return; // Don't initialize in browser
    }

    try {
      // Use the safe import utility instead of direct Function approach
      const redisModule = await safeNodeImport<RedisModule>('ioredis');
      const env = getEnvironment();

      // Configure Redis client based on environment
      if (env.isLocalDevelopment || env.isPodmanTest) {
        this.client = new redisModule.default({
          host: env.redisHost || 'localhost',
          port: env.redisPort || 6379,
          maxRetriesPerRequest: 3,
          retryStrategy: (times: number) => Math.min(times * 50, 2000),
        });
      } else {
        // Production configuration
        this.client = new redisModule.default(env.redisUrl || '');
      }
    } catch (error) {
      console.error('Failed to import Redis module:', error);
    }
  }

  async get(key: string): Promise<string | null> {
    if (!this.client) return null;
    return this.client.get(key);
  }

  async set(key: string, value: string, ttlSeconds?: number): Promise<void> {
    if (!this.client) return;

    if (ttlSeconds) {
      // Set with expiration if TTL provided
      await this.client.set(key, value, 'EX', ttlSeconds);
    } else {
      // Set without expiration
      await this.client.set(key, value);
    }
  }

  async del(key: string): Promise<void> {
    if (!this.client) return;
    await this.client.del(key);
  }
}
